"use client";

/**
 * Small ILLUSTRATIONS (not line icons) for the home "what we offer" tool
 * rows: Compare / Alerts / Watchlist / Invest / My properties. Each is a
 * 48x48 mini-scene that is RELEVANT to the feature and THEME-SAFE:
 * transparent background, so the well's adaptive surface shows through in
 * both light and dark mode. Shapes use mid-indigo + one accent.
 *
 * Craft pass (uxpeak techniques, ported to the light theme):
 * - blockBuild: bars/buildings grow from the ground via scaleY with a
 *   slight overshoot, staggered left → right
 * - drawn strokes: the invest arrow draws itself via stroke-dashoffset
 * - spring pops (sCheckPop): badges/bookmarks land with a bounce
 * - sRipple: alert rings expand + fade from the bell
 * - sparkle: a twinkle after the bookmark lands
 * - every loop holds, fades, and pauses briefly at the seam (6-7s cycles)
 *
 * transform/opacity/stroke-dashoffset-only motion, pure CSS keyframes,
 * memo() wrapped, and a reduced-motion resting frame (final state).
 * Fills its well (h-full w-full).
 */
import { memo, type SVGProps } from "react";
import { useScenePause } from "./useScenePause";

const BOX: SVGProps<SVGSVGElement> = {
  viewBox: "0 0 48 48",
  className: "scene-svg h-full w-full",
  "aria-hidden": true,
};

const INDIGO = "#6366f1";
const INDIGO_D = "#4f46e5";
const EMERALD = "#10b981";
const ROSE = "#f43f5e";
const AMBER = "#f59e0b";

// A lit window - white on the building, reads on light + dark wells alike.
const Win = ({ x, y, className }: { x: number; y: number; className?: string }) => (
  <rect className={className} x={x} y={y} width="3" height="3" rx="0.6" fill="#ffffff" fillOpacity="0.9" />
);

/* Compare - two apartment buildings BUILD UP from the ground (staggered
   scaleY with overshoot), then trade a gentle counter-bob; the better-value
   emerald tower lands a springing price-drop badge. 7s loop. */
function CompareAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      {/* left building */}
      <g className="cmp-l">
        <rect x="9" y="19" width="13" height="20" rx="1.5" fill={INDIGO} />
        <Win x={11.5} y={22} /><Win x={16.5} y={22} />
        <Win x={11.5} y={27} /><Win x={16.5} y={27} />
        <Win x={11.5} y={32} /><Win x={16.5} y={32} />
      </g>
      {/* right building - the better deal */}
      <g className="cmp-r">
        <rect x="26" y="15" width="13" height="24" rx="1.5" fill={EMERALD} />
        <Win x={28.5} y={18} /><Win x={33.5} y={18} />
        <Win x={28.5} y={23} /><Win x={33.5} y={23} />
        <Win x={28.5} y={28} /><Win x={33.5} y={28} />
        <Win x={28.5} y={33} /><Win x={33.5} y={33} />
      </g>
      {/* price-drop badge - springs in once the tower is up */}
      <g className="cmp-badge">
        <circle cx="38.5" cy="13" r="5" fill="#ffffff" stroke={EMERALD} strokeWidth="1.4" />
        <path d="M38.5 10.8 v3.8 M36.8 13.1 l1.7 1.7 l1.7 -1.7" fill="none" stroke={EMERALD} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <style>{`
        @keyframes cmpL {
          0%, 2%    { opacity: 0; transform: scaleY(0); }
          10%       { opacity: 1; transform: scaleY(1.05); }
          14%       { transform: scaleY(1) translateY(0); }
          45%       { transform: scaleY(1) translateY(1.2px); }
          70%       { transform: scaleY(1) translateY(0); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        @keyframes cmpR {
          0%, 8%    { opacity: 0; transform: scaleY(0); }
          16%       { opacity: 1; transform: scaleY(1.05); }
          20%       { transform: scaleY(1) translateY(0); }
          45%       { transform: scaleY(1) translateY(-2px); }
          70%       { transform: scaleY(1) translateY(0); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        @keyframes cmpBadge {
          0%, 20%   { opacity: 0; transform: scale(0.3); }
          28%       { opacity: 1; transform: scale(1.2); }
          33%       { transform: scale(1); }
          90%       { opacity: 1; transform: scale(1); }
          96%, 100% { opacity: 0; transform: scale(1); }
        }
        .cmp-l     { transform-box: fill-box; transform-origin: center bottom; animation: cmpL 7s cubic-bezier(.22,1,.36,1) infinite; }
        .cmp-r     { transform-box: fill-box; transform-origin: center bottom; animation: cmpR 7s cubic-bezier(.22,1,.36,1) infinite; }
        .cmp-badge { transform-box: fill-box; transform-origin: center; animation: cmpBadge 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .cmp-l, .cmp-r, .cmp-badge { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

/* Alerts - a filled bell rings while a rose price-alert badge springs in
   and RIPPLE RINGS (sRipple) expand + fade around the bell. 6s loop. */
function AlertsAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      <defs>
        <linearGradient id="toolAlBell" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={INDIGO} />
          <stop offset="1" stopColor={INDIGO_D} />
        </linearGradient>
      </defs>
      {/* ripples behind the bell */}
      <circle className="al-rip al-rip-1" cx="24" cy="22" r="13" fill="none" stroke={INDIGO} strokeWidth="1.3" />
      <circle className="al-rip al-rip-2" cx="24" cy="22" r="13" fill="none" stroke={INDIGO} strokeWidth="1.3" />
      <g className="al-bell">
        <circle cx="24" cy="12.5" r="1.7" fill={INDIGO_D} />
        <path d="M17 20.5 a7 7 0 0 1 14 0 c0 6 2 7.5 2 9.5 H15 c0 -2 2 -3.5 2 -9.5 Z" fill="url(#toolAlBell)" />
        <path d="M21.3 32 a2.7 2.7 0 0 0 5.4 0" fill="none" stroke={INDIGO_D} strokeWidth="2.2" strokeLinecap="round" />
      </g>
      <circle className="al-dot" cx="31.5" cy="15" r="4.6" fill={ROSE} stroke="#ffffff" strokeWidth="1.7" />
      <style>{`
        @keyframes alBell {
          0%, 18%, 62%, 100% { transform: rotate(0); }
          26% { transform: rotate(12deg); }
          36% { transform: rotate(-9deg); }
          46% { transform: rotate(5deg); }
          54% { transform: rotate(-2deg); }
        }
        @keyframes alDot {
          0%, 12%   { transform: scale(0); opacity: 0; }
          20%       { transform: scale(1.25); opacity: 1; }
          26%, 100% { transform: scale(1); opacity: 1; }
        }
        @keyframes alRip1 {
          0%, 20%   { opacity: 0; transform: scale(0.55); }
          24%       { opacity: 0.5; transform: scale(0.7); }
          46%, 100% { opacity: 0; transform: scale(1.5); }
        }
        @keyframes alRip2 {
          0%, 30%   { opacity: 0; transform: scale(0.55); }
          34%       { opacity: 0.4; transform: scale(0.7); }
          58%, 100% { opacity: 0; transform: scale(1.6); }
        }
        .al-bell { transform-box: fill-box; transform-origin: 50% 8%; animation: alBell 6s ease-in-out infinite; }
        .al-dot  { transform-box: fill-box; transform-origin: center; animation: alDot 6s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        .al-rip  { transform-box: fill-box; transform-origin: center; opacity: 0; }
        .al-rip-1 { animation: alRip1 6s ease-out infinite; }
        .al-rip-2 { animation: alRip2 6s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .al-bell, .al-rip-1, .al-rip-2 { animation: none; }
          .al-rip { opacity: 0; }
          .al-dot { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

/* Watchlist - an apartment building you've saved: an amber bookmark ribbon
   springs onto its corner, then a small sparkle twinkles beside it. */
function WatchlistAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      <rect x="11" y="14" width="18" height="25" rx="1.5" fill={INDIGO} />
      <Win x={14} y={18} /><Win x={18.5} y={18} /><Win x={23} y={18} />
      <Win x={14} y={23} /><Win x={18.5} y={23} /><Win x={23} y={23} />
      <Win x={14} y={28} /><Win x={18.5} y={28} /><Win x={23} y={28} />
      <Win x={14} y={33} /><Win x={18.5} y={33} /><Win x={23} y={33} />
      {/* a shorter neighbour tower, for a little skyline depth */}
      <rect x="30.5" y="22" width="7" height="17" rx="1.5" fill={INDIGO} fillOpacity="0.55" />
      <g className="wl-mark">
        <path d="M28 12 h7 v9 l-3.5 -2.4 l-3.5 2.4 V12 Z" fill={AMBER} stroke="#ffffff" strokeWidth="1.1" strokeLinejoin="round" />
      </g>
      {/* twinkle after the bookmark lands */}
      <path
        className="wl-spark"
        d="M40 20 l1 2.4 l2.4 1 l-2.4 1 l-1 2.4 l-1 -2.4 l-2.4 -1 l2.4 -1 Z"
        fill={AMBER}
      />
      <style>{`
        @keyframes wlMark {
          0%, 8%    { opacity: 0; transform: translateY(-4px) scale(0.7); }
          18%       { opacity: 1; transform: translateY(0) scale(1.12); }
          26%, 100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wlSpark {
          0%, 28%   { opacity: 0; transform: scale(0.4) rotate(0deg); }
          38%       { opacity: 1; transform: scale(1.1) rotate(90deg); }
          52%, 100% { opacity: 0; transform: scale(0.5) rotate(180deg); }
        }
        .wl-mark  { transform-box: fill-box; transform-origin: center top; animation: wlMark 6.8s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        .wl-spark { transform-box: fill-box; transform-origin: center; animation: wlSpark 6.8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wl-mark { animation: none; opacity: 1; transform: none; }
          .wl-spark { animation: none; opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

/* Invest / yield - the bars BUILD UP from the ground one by one (the
   blockBuild scaleY pattern with overshoot), then the emerald trend arrow
   DRAWS ITSELF over them via stroke-dashoffset. 6.5s loop. */
function InvestAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      {/* Three bars, tallest on the right - staggered blockBuild */}
      <rect className="inv-b inv-b0" x="9"  y="28" width="8" height="11" rx="1.5" fill={INDIGO} />
      <rect className="inv-b inv-b1" x="20" y="22" width="8" height="17" rx="1.5" fill={INDIGO_D} />
      <rect className="inv-b inv-b2" x="31" y="14" width="8" height="25" rx="1.5" fill={EMERALD} />
      {/* Rising arrow - draws in after the bars are up */}
      <polyline
        className="inv-line"
        points="9,26 20,20 31,12"
        fill="none"
        stroke={EMERALD}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="27"
        strokeDashoffset="27"
      />
      <polyline
        className="inv-head"
        points="26,10 31,12 29,17"
        fill="none"
        stroke={EMERALD}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="11"
        strokeDashoffset="11"
      />
      <style>{`
        @keyframes invB0 {
          0%, 4%    { opacity: 0; transform: scaleY(0); }
          12%       { opacity: 1; transform: scaleY(1.08); }
          16%       { transform: scaleY(1); }
          90%       { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes invB1 {
          0%, 10%   { opacity: 0; transform: scaleY(0); }
          18%       { opacity: 1; transform: scaleY(1.08); }
          22%       { transform: scaleY(1); }
          90%       { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes invB2 {
          0%, 16%   { opacity: 0; transform: scaleY(0); }
          24%       { opacity: 1; transform: scaleY(1.08); }
          28%       { transform: scaleY(1); }
          90%       { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes invLine {
          0%, 28%   { stroke-dashoffset: 27; opacity: 1; }
          42%       { stroke-dashoffset: 0; }
          90%       { stroke-dashoffset: 0; opacity: 1; }
          96%, 100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes invHead {
          0%, 40%   { stroke-dashoffset: 11; opacity: 1; }
          48%       { stroke-dashoffset: 0; }
          90%       { stroke-dashoffset: 0; opacity: 1; }
          96%, 100% { stroke-dashoffset: 0; opacity: 0; }
        }
        .inv-b    { transform-box: fill-box; transform-origin: center bottom; }
        .inv-b0   { animation: invB0 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        .inv-b1   { animation: invB1 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        .inv-b2   { animation: invB2 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        .inv-line { animation: invLine 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        .inv-head { animation: invHead 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .inv-b0, .inv-b1, .inv-b2, .inv-line, .inv-head { animation: none; opacity: 1; transform: none; }
          .inv-line, .inv-head { stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  );
}

/* My Properties / portfolio - your own building wakes up: the windows
   LIGHT UP floor by floor, then an emerald value badge (AZN) springs in
   and gently bobs: "your property, its value tracked". 7s loop. */
function PropertiesAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      <rect x="10" y="15" width="18" height="24" rx="1.5" fill={INDIGO} />
      <Win className="pr-w pr-w0" x={12.5} y={19} /><Win className="pr-w pr-w0" x={17} y={19} /><Win className="pr-w pr-w0" x={21.5} y={19} />
      <Win className="pr-w pr-w1" x={12.5} y={24} /><Win className="pr-w pr-w1" x={17} y={24} /><Win className="pr-w pr-w1" x={21.5} y={24} />
      <Win className="pr-w pr-w2" x={12.5} y={29} /><Win className="pr-w pr-w2" x={17} y={29} /><Win className="pr-w pr-w2" x={21.5} y={29} />
      <Win className="pr-w pr-w3" x={12.5} y={34} /><Win className="pr-w pr-w3" x={17} y={34} /><Win className="pr-w pr-w3" x={21.5} y={34} />
      <g className="prop-badge">
        <circle cx="34" cy="14" r="6.5" fill={EMERALD} stroke="#ffffff" strokeWidth="1.5" />
        <text x="34" y="17.3" textAnchor="middle" fontSize="9" fontWeight="700" fill="#ffffff">AZN</text>
      </g>
      <style>{`
        /* floors light top → bottom, hold lit, dim at the seam */
        @keyframes prW0 {
          0%, 6%    { opacity: 0.18; }
          12%, 90%  { opacity: 1; }
          96%, 100% { opacity: 0.18; }
        }
        @keyframes prW1 {
          0%, 11%   { opacity: 0.18; }
          17%, 90%  { opacity: 1; }
          96%, 100% { opacity: 0.18; }
        }
        @keyframes prW2 {
          0%, 16%   { opacity: 0.18; }
          22%, 90%  { opacity: 1; }
          96%, 100% { opacity: 0.18; }
        }
        @keyframes prW3 {
          0%, 21%   { opacity: 0.18; }
          27%, 90%  { opacity: 1; }
          96%, 100% { opacity: 0.18; }
        }
        @keyframes propBadge {
          0%, 26%   { opacity: 0; transform: scale(0.3) translateY(0); }
          34%       { opacity: 1; transform: scale(1.18) translateY(0); }
          40%       { transform: scale(1) translateY(0); }
          58%       { transform: scale(1) translateY(-2px); }
          76%       { transform: scale(1) translateY(0); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        .pr-w0 { animation: prW0 7s ease-in-out infinite; }
        .pr-w1 { animation: prW1 7s ease-in-out infinite; }
        .pr-w2 { animation: prW2 7s ease-in-out infinite; }
        .pr-w3 { animation: prW3 7s ease-in-out infinite; }
        .prop-badge { transform-box: fill-box; transform-origin: center; animation: propBadge 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pr-w0, .pr-w1, .pr-w2, .pr-w3, .prop-badge { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}


/* Calculators - a pocket calculator taps out a price: three keys press
   in sequence, digits appear on the display with each tap, and an
   emerald "=" result chip springs in. 6.5s loop, seam-faded. */
function CalcAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      <g className="clc-body">
        {/* body + display */}
        <rect x="12" y="7" width="24" height="34" rx="4" fill={INDIGO} />
        <rect x="15.5" y="11" width="17" height="7" rx="1.5" fill="#ffffff" fillOpacity="0.92" />
        {/* typed digits - land one per key press */}
        <rect className="clc-d clc-d1" x="17.5" y="13.4" width="3" height="2.2" rx="0.6" fill={INDIGO_D} />
        <rect className="clc-d clc-d2" x="21.5" y="13.4" width="3" height="2.2" rx="0.6" fill={INDIGO_D} />
        <rect className="clc-d clc-d3" x="25.5" y="13.4" width="3" height="2.2" rx="0.6" fill={INDIGO_D} />
        {/* 3x3 keypad; three keys "press" in sequence */}
        {[0, 1, 2].map((r) =>
          [0, 1, 2].map((c) => (
            <rect
              key={`k${r}${c}`}
              className={
                r === 0 && c === 0 ? "clc-k clc-k1"
                : r === 1 && c === 1 ? "clc-k clc-k2"
                : r === 2 && c === 2 ? "clc-k clc-k3"
                : undefined
              }
              x={16.2 + c * 5.6}
              y={21.5 + r * 5.6}
              width="4.2"
              height="4"
              rx="1"
              fill="#ffffff"
              fillOpacity="0.88"
            />
          )),
        )}
      </g>
      {/* result chip - springs in after the taps */}
      <g className="clc-badge">
        <circle cx="37" cy="38" r="6" fill="#ffffff" stroke={EMERALD} strokeWidth="1.4" />
        <path d="M34.4 36.6 h5.2 M34.4 39.4 h5.2" stroke={EMERALD} strokeWidth="1.4" strokeLinecap="round" />
      </g>
      <style>{`
        @keyframes clcBody {
          0%, 2%    { opacity: 0; transform: translateY(3px); }
          8%        { opacity: 1; transform: translateY(0); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        @keyframes clcKey {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(0.78); }
        }
        @keyframes clcDigit {
          0%   { opacity: 0; transform: translateY(1.5px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes clcBadge {
          0%, 40%   { opacity: 0; transform: scale(0); }
          48%       { opacity: 1; transform: scale(1.18); }
          54%       { transform: scale(1); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        .clc-body  { animation: clcBody 6.5s ease-in-out infinite; }
        .clc-k     { transform-box: fill-box; transform-origin: center; }
        .clc-k1    { animation: clcKey 6.5s ease-in-out infinite; animation-delay: 0.8s; }
        .clc-k2    { animation: clcKey 6.5s ease-in-out infinite; animation-delay: 1.4s; }
        .clc-k3    { animation: clcKey 6.5s ease-in-out infinite; animation-delay: 2.0s; }
        .clc-d     { opacity: 0; transform-box: fill-box; }
        .clc-d1    { animation: clcDigit 0.3s ease-out 1.0s forwards; }
        .clc-d2    { animation: clcDigit 0.3s ease-out 1.6s forwards; }
        .clc-d3    { animation: clcDigit 0.3s ease-out 2.2s forwards; }
        .clc-badge { transform-box: fill-box; transform-origin: center; animation: clcBadge 6.5s cubic-bezier(.34,1.56,.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .clc-body, .clc-k1, .clc-k2, .clc-k3, .clc-badge { animation: none; }
          .clc-body { opacity: 1; transform: none; }
          .clc-d { opacity: 1; animation: none; }
          .clc-badge { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </svg>
  );
}

/* Home Finder - a house BUILDS UP from the ground (blockBuild), a
   magnifier sweeps across it hunting, then an emerald location pin
   DROPS onto the roof with a spring pop: "we found where". 7s loop. */
function FinderAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg {...BOX} ref={sceneRef}>
      {/* the house */}
      <g className="fnd-house">
        <rect x="14" y="24" width="20" height="15" rx="1.5" fill={INDIGO} />
        <path d="M12 25 L24 15 L36 25 Z" fill={INDIGO_D} />
        <Win x={17.5} y={28} /><Win x={27.5} y={28} />
        <rect x="22" y="31" width="4" height="8" rx="0.8" fill="#ffffff" fillOpacity="0.9" />
      </g>
      {/* sweeping magnifier */}
      <g className="fnd-glass">
        <circle cx="20" cy="20" r="5.5" fill="none" stroke={AMBER} strokeWidth="1.8" />
        <path d="M24.2 24.2 L28 28" stroke={AMBER} strokeWidth="1.8" strokeLinecap="round" />
      </g>
      {/* the "found it" pin */}
      <g className="fnd-pin">
        <path
          d="M38 8 c-2.8 0 -5 2.2 -5 5 c0 3.6 5 8.4 5 8.4 s5 -4.8 5 -8.4 c0 -2.8 -2.2 -5 -5 -5 z"
          fill={EMERALD}
        />
        <circle cx="38" cy="13" r="2" fill="#ffffff" />
      </g>
      <style>{`
        @keyframes fndHouse {
          0%, 2%    { opacity: 0; transform: scaleY(0); }
          10%       { opacity: 1; transform: scaleY(1.05); }
          14%       { transform: scaleY(1); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        @keyframes fndGlass {
          0%, 12%   { opacity: 0; transform: translate(0, 0); }
          18%       { opacity: 1; }
          32%       { transform: translate(8px, 6px); }
          46%       { transform: translate(-2px, 10px); }
          56%       { opacity: 1; transform: translate(4px, 4px); }
          62%, 100% { opacity: 0; transform: translate(4px, 4px); }
        }
        @keyframes fndPin {
          0%, 58%   { opacity: 0; transform: translateY(-10px) scale(0.4); }
          66%       { opacity: 1; transform: translateY(0) scale(1.15); }
          72%       { transform: translateY(0) scale(1); }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        .fnd-house { transform-box: fill-box; transform-origin: center bottom; animation: fndHouse 7s cubic-bezier(.22,1,.36,1) infinite; }
        .fnd-glass { transform-box: fill-box; transform-origin: center; animation: fndGlass 7s ease-in-out infinite; }
        .fnd-pin   { transform-box: fill-box; transform-origin: center bottom; animation: fndPin 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .fnd-house, .fnd-glass, .fnd-pin { animation: none; opacity: 1; transform: none; }
          .fnd-glass { opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

export const CompareAnim = memo(CompareAnimBase);
export const CalcAnim = memo(CalcAnimBase);
export const PropertiesAnim = memo(PropertiesAnimBase);
export const AlertsAnim = memo(AlertsAnimBase);
export const WatchlistAnim = memo(WatchlistAnimBase);
export const InvestAnim = memo(InvestAnimBase);
/** @public - waits for the finder entry points, parked 2026-09-20 */
export const FinderAnim = memo(FinderAnimBase);
